import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import {
  BrainCircuit,
  AlertTriangle,
  TrendingDown,
  Clock,
  Target,
  Activity,
  Thermometer,
  Volume2,
  Gauge,
  CheckCircle2,
  ArrowRight,
} from 'lucide-react';
import {
  predictiveAssets,
  sensorData,
  aiRecommendations,
  maintenanceComparison,
} from '@/data/mockData';

/* ───────── StatCard (inline, same pattern as DashboardPage) ───────── */
const StatCard = ({ title, value, subtitle, icon: Icon, trend, variant = 'default' }) => {
  const variantStyles = {
    default: 'border-border',
    success: 'border-success border-2',
    warning: 'border-warning border-2',
    danger: 'border-destructive border-2',
    info: 'border-info border-2',
  };
  return (
    <Card className={`shadow-soft ${variantStyles[variant]}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <div className="flex items-baseline gap-2">
              <p className="text-3xl font-bold text-foreground">{value}</p>
              {trend && (
                <span className={`text-sm font-medium ${trend.startsWith('+') ? 'text-success' : trend.startsWith('-') ? 'text-success' : 'text-destructive'}`}>
                  {trend}
                </span>
              )}
            </div>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          {Icon && (
            <div className="p-3 bg-primary-light rounded-lg">
              <Icon className="w-6 h-6 text-primary" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

/* ───────── SVG Mini Line Chart ───────── */
const SvgLineChart = ({ data, warningThreshold, criticalThreshold, color, label, unit }) => {
  const width = 320;
  const height = 120;
  const padX = 32;
  const padY = 16;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;

  const vals = data.map((d) => d.value);
  const minV = Math.min(...vals, warningThreshold ?? Infinity, criticalThreshold ?? Infinity) * 0.92;
  const maxV = Math.max(...vals, warningThreshold ?? -Infinity, criticalThreshold ?? -Infinity) * 1.08;
  const range = maxV - minV || 1;

  const toX = (i) => padX + (i / (data.length - 1)) * innerW;
  const toY = (v) => padY + innerH - ((v - minV) / range) * innerH;

  const points = data.map((d, i) => `${toX(i).toFixed(1)},${toY(d.value).toFixed(1)}`).join(' ');

  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-1">{label} ({unit})</p>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ maxHeight: height }}>
        {/* warning threshold */}
        {warningThreshold != null && (
          <line
            x1={padX} y1={toY(warningThreshold)} x2={width - padX} y2={toY(warningThreshold)}
            stroke="hsl(var(--warning))" strokeWidth="1" strokeDasharray="6 3" opacity="0.7"
          />
        )}
        {/* critical threshold */}
        {criticalThreshold != null && (
          <line
            x1={padX} y1={toY(criticalThreshold)} x2={width - padX} y2={toY(criticalThreshold)}
            stroke="hsl(var(--destructive))" strokeWidth="1" strokeDasharray="6 3" opacity="0.7"
          />
        )}
        {/* data line */}
        <polyline fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" points={points} />
        {/* dots on last point */}
        {data.length > 0 && (
          <circle cx={toX(data.length - 1)} cy={toY(vals[vals.length - 1])} r="3" fill={color} />
        )}
        {/* y-axis labels */}
        <text x={padX - 4} y={padY + 4} textAnchor="end" className="fill-muted-foreground" style={{ fontSize: 9 }}>
          {maxV.toFixed(1)}
        </text>
        <text x={padX - 4} y={height - padY + 4} textAnchor="end" className="fill-muted-foreground" style={{ fontSize: 9 }}>
          {minV.toFixed(1)}
        </text>
        {/* x-axis labels */}
        <text x={padX} y={height - 2} textAnchor="start" className="fill-muted-foreground" style={{ fontSize: 9 }}>
          W1
        </text>
        <text x={width - padX} y={height - 2} textAnchor="end" className="fill-muted-foreground" style={{ fontSize: 9 }}>
          W26
        </text>
      </svg>
    </div>
  );
};

/* ───────── RUL Circular Indicator ───────── */
const RulCircle = ({ rul, maxRul = 180, t }) => {
  const pct = Math.min(100, (rul / maxRul) * 100);
  const radius = 40;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (pct / 100) * circ;
  const circleColor = pct > 60 ? 'hsl(var(--success))' : pct > 30 ? 'hsl(var(--warning))' : 'hsl(var(--destructive))';

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width="96" height="96" viewBox="0 0 96 96">
        <circle cx="48" cy="48" r={radius} fill="none" stroke="hsl(var(--muted))" strokeWidth="6" />
        <circle
          cx="48" cy="48" r={radius} fill="none" stroke={circleColor} strokeWidth="6"
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
          transform="rotate(-90 48 48)"
        />
        <text x="48" y="44" textAnchor="middle" className="fill-foreground font-bold" style={{ fontSize: 18 }}>
          {rul}
        </text>
        <text x="48" y="58" textAnchor="middle" className="fill-muted-foreground" style={{ fontSize: 9 }}>
          {t('daysRemaining')}
        </text>
      </svg>
    </div>
  );
};

/* ───────── Tabs (lightweight inline) ───────── */
const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
      active
        ? 'bg-primary text-primary-foreground shadow-sm'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
    }`}
  >
    {children}
  </button>
);

/* ───────── Health Status Badge ───────── */
const statusVariant = (s) => (s === 'ok' ? 'success' : s === 'warning' ? 'warning' : 'destructive');
const statusLabel = (s, t) => (s === 'ok' ? t('operational') : s === 'warning' ? t('maintenance') : t('critical'));

/* ───────── Main Page ───────── */
export const PredictiveMaintenancePage = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('sensors');
  const [selectedAssetId, setSelectedAssetId] = useState(predictiveAssets[0]?.id);

  const selectedAsset = predictiveAssets.find((a) => a.id === selectedAssetId) || predictiveAssets[0];
  const selectedSensors = sensorData[selectedAsset.id] || [];

  // Stats
  const criticalIn7d = predictiveAssets.filter((a) => a.rul <= 30 && a.status !== 'ok').length;
  const savingsAmount = ((maintenanceComparison.preventive.annualCost - maintenanceComparison.predictive.annualCost) / 1000000).toFixed(1);
  const downtimeReduction = maintenanceComparison.predictive.unplannedDowntimeHours;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-3">
          <BrainCircuit className="w-8 h-8 text-primary" />
          {t('predictiveMaintenance')}
        </h1>
        <p className="text-muted-foreground">{t('predictiveSubtitle')}</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title={t('predictedFailures7d')}
          value={criticalIn7d}
          subtitle={`${predictiveAssets.length} ${t('totalAssets').toLowerCase()}`}
          icon={AlertTriangle}
          variant="danger"
        />
        <StatCard
          title={t('avgPredictionAccuracy')}
          value="87%"
          trend="+3%"
          subtitle="ML model v2.4"
          icon={Target}
          variant="success"
        />
        <StatCard
          title={t('savingsVsPreventive')}
          value={`${savingsAmount}M`}
          subtitle="CZK / rok"
          icon={TrendingDown}
          variant="info"
        />
        <StatCard
          title={t('unplannedDowntime')}
          value={`${downtimeReduction}h`}
          trend="-85%"
          subtitle="vs preventivni"
          icon={Clock}
          variant="warning"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        <TabButton active={activeTab === 'sensors'} onClick={() => setActiveTab('sensors')}>{t('sensorDashboard')}</TabButton>
        <TabButton active={activeTab === 'recommendations'} onClick={() => setActiveTab('recommendations')}>{t('aiRecommendations')}</TabButton>
        <TabButton active={activeTab === 'timeline'} onClick={() => setActiveTab('timeline')}>{t('assetHealthTimeline')}</TabButton>
        <TabButton active={activeTab === 'comparison'} onClick={() => setActiveTab('comparison')}>{t('comparisonView')}</TabButton>
      </div>

      {/* Tab Content */}
      {activeTab === 'sensors' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Asset list (left panel) */}
          <div className="space-y-3">
            {predictiveAssets.map((asset) => (
              <Card
                key={asset.id}
                className={`shadow-soft cursor-pointer transition-all hover:shadow-md ${
                  selectedAssetId === asset.id ? 'ring-2 ring-primary' : ''
                }`}
                onClick={() => setSelectedAssetId(asset.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{asset.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{asset.location}</p>
                    </div>
                    <Badge variant={statusVariant(asset.status)}>{statusLabel(asset.status, t)}</Badge>
                  </div>
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Health</span>
                      <span className="font-medium text-foreground">{asset.currentHealth}%</span>
                    </div>
                    <Progress value={asset.currentHealth} className="h-1.5" />
                    <p className="text-xs text-muted-foreground">
                      RUL: <span className="font-medium text-foreground">{asset.rul} {t('daysRemaining')}</span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Sensor charts (right panel 2/3) */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="shadow-soft">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="w-5 h-5 text-primary" />
                  {selectedAsset.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <SvgLineChart
                    data={selectedSensors.map((s) => ({ value: s.vibration }))}
                    warningThreshold={3.5}
                    criticalThreshold={4.5}
                    color="hsl(var(--primary))"
                    label={t('vibration')}
                    unit="mm/s"
                  />
                  <SvgLineChart
                    data={selectedSensors.map((s) => ({ value: s.temperature }))}
                    warningThreshold={85}
                    criticalThreshold={95}
                    color="hsl(var(--destructive))"
                    label={t('temperatureSensor')}
                    unit="°C"
                  />
                  <SvgLineChart
                    data={selectedSensors.map((s) => ({ value: s.acoustic }))}
                    warningThreshold={78}
                    criticalThreshold={85}
                    color="hsl(var(--warning))"
                    label={t('acousticEmission')}
                    unit="dB"
                  />
                  <SvgLineChart
                    data={selectedSensors.map((s) => ({ value: s.pressure }))}
                    warningThreshold={5.0}
                    criticalThreshold={4.5}
                    color="hsl(var(--info))"
                    label={t('pressureSensor')}
                    unit="bar"
                  />
                </div>
                {/* RUL circle */}
                <div className="mt-6 flex items-center justify-center gap-8 pt-4 border-t border-border">
                  <RulCircle rul={selectedAsset.rul} t={t} />
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">{t('failureProbability')}</p>
                    <p className="text-2xl font-bold text-foreground">{(selectedAsset.failureProbability * 100).toFixed(0)}%</p>
                    <p className="text-xs text-muted-foreground">
                      {t('predictedFailures7d').split('(')[0].trim()}: {selectedAsset.predictedFailureDate}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'recommendations' && (
        <div className="space-y-4">
          {aiRecommendations.map((rec) => (
            <Card key={rec.id} className={`shadow-soft ${rec.severity === 'critical' ? 'border-destructive border-2' : rec.severity === 'warning' ? 'border-warning border-2' : 'border-border'}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={rec.severity === 'critical' ? 'destructive' : rec.severity === 'warning' ? 'warning' : 'default'}>
                        {rec.severity === 'critical' ? t('critical') : rec.severity === 'warning' ? t('maintenance') : 'Info'}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{rec.assetName}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">{rec.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{rec.description}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-muted-foreground">{t('estimatedSavings')}</p>
                    <p className="text-xl font-bold text-success">{(rec.estimatedSavings / 1000000).toFixed(1)}M CZK</p>
                  </div>
                </div>
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-sm font-medium text-foreground mb-2">{t('recommendedActions')}</p>
                  <ol className="space-y-1.5">
                    {rec.actions.map((action, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium shrink-0 mt-0.5">
                          {i + 1}
                        </span>
                        {action}
                      </li>
                    ))}
                  </ol>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {activeTab === 'timeline' && (
        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              {t('assetHealthTimeline')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <HealthTimelineSvg assets={predictiveAssets} sensorData={sensorData} t={t} />
          </CardContent>
        </Card>
      )}

      {activeTab === 'comparison' && (
        <ComparisonTab t={t} comparison={maintenanceComparison} />
      )}
    </div>
  );
};

/* ───────── Health Timeline SVG ───────── */
const HealthTimelineSvg = ({ assets, sensorData: sd, t }) => {
  const [hoveredAsset, setHoveredAsset] = useState(null);
  const width = 700;
  const height = 320;
  const padX = 50;
  const padY = 30;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;

  const colors = ['hsl(var(--primary))', 'hsl(var(--destructive))', 'hsl(var(--warning))', 'hsl(var(--info))', 'hsl(var(--success))', '#a855f7'];

  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ maxHeight: 340 }}>
        {/* grid */}
        {[0, 25, 50, 75, 100].map((v) => {
          const y = padY + innerH - (v / 100) * innerH;
          return (
            <g key={v}>
              <line x1={padX} y1={y} x2={width - padX} y2={y} stroke="hsl(var(--border))" strokeWidth="0.5" />
              <text x={padX - 6} y={y + 3} textAnchor="end" className="fill-muted-foreground" style={{ fontSize: 9 }}>{v}%</text>
            </g>
          );
        })}
        {/* x-axis labels */}
        {[0, 6, 13, 19, 25].map((w) => {
          const x = padX + (w / 25) * innerW;
          return (
            <text key={w} x={x} y={height - 4} textAnchor="middle" className="fill-muted-foreground" style={{ fontSize: 9 }}>
              W{w + 1}
            </text>
          );
        })}
        {/* lines per asset */}
        {assets.map((asset, ai) => {
          const weeks = sd[asset.id] || [];
          // Derive health from sensor data: simple formula
          const healthPoints = weeks.map((w, wi) => {
            const h = Math.max(0, Math.min(100, 100 - ((w.vibration - 2) * 12 + (w.temperature - 72) * 0.8)));
            return { week: wi, health: h };
          });
          // Extend with dashed prediction
          const knownCount = 20;
          const knownPts = healthPoints.slice(0, knownCount);
          const predPts = healthPoints.slice(knownCount - 1);

          const toX = (wi) => padX + (wi / 25) * innerW;
          const toY = (h) => padY + innerH - (h / 100) * innerH;

          const knownLine = knownPts.map((p) => `${toX(p.week).toFixed(1)},${toY(p.health).toFixed(1)}`).join(' ');
          const predLine = predPts.map((p) => `${toX(p.week).toFixed(1)},${toY(p.health).toFixed(1)}`).join(' ');

          const isHovered = hoveredAsset === asset.id;
          const opacity = hoveredAsset == null ? 0.8 : isHovered ? 1 : 0.2;

          return (
            <g
              key={asset.id}
              onMouseEnter={() => setHoveredAsset(asset.id)}
              onMouseLeave={() => setHoveredAsset(null)}
              style={{ cursor: 'pointer' }}
            >
              <polyline fill="none" stroke={colors[ai]} strokeWidth={isHovered ? 3 : 2} opacity={opacity} strokeLinejoin="round" points={knownLine} />
              {predPts.length > 1 && (
                <polyline fill="none" stroke={colors[ai]} strokeWidth={isHovered ? 3 : 2} opacity={opacity} strokeLinejoin="round" strokeDasharray="6 4" points={predLine} />
              )}
              {/* failure point */}
              {asset.status === 'critical' && healthPoints.length > 0 && (
                <circle
                  cx={toX(healthPoints[healthPoints.length - 1].week)}
                  cy={toY(healthPoints[healthPoints.length - 1].health)}
                  r={isHovered ? 5 : 4}
                  fill={colors[ai]}
                  opacity={opacity}
                />
              )}
            </g>
          );
        })}
      </svg>
      {/* Legend */}
      <div className="flex flex-wrap gap-4 mt-3 pt-3 border-t border-border">
        {assets.map((asset, ai) => (
          <div
            key={asset.id}
            className="flex items-center gap-2 text-xs cursor-pointer"
            onMouseEnter={() => setHoveredAsset(asset.id)}
            onMouseLeave={() => setHoveredAsset(null)}
          >
            <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: colors[ai] }} />
            <span className="text-muted-foreground">{asset.name}</span>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
        <span className="inline-block w-6 border-t-2 border-dashed border-muted-foreground" /> = {t('remainingLife')} ({t('failureProbability').toLowerCase()})
      </p>
    </div>
  );
};

/* ───────── Comparison Tab ───────── */
const ComparisonTab = ({ t, comparison }) => {
  const methods = [
    { key: 'preventive', label: t('preventiveLabel'), color: 'hsl(var(--muted-foreground))' },
    { key: 'predictive', label: t('predictiveLabel'), color: 'hsl(var(--primary))' },
    { key: 'prescriptive', label: t('prescriptiveLabel'), color: 'hsl(var(--success))' },
  ];

  const maxCost = Math.max(...methods.map((m) => comparison[m.key].annualCost));

  const metrics = [
    { key: 'annualCost', label: t('annualCost'), format: (v) => `${(v / 1000000).toFixed(1)}M CZK` },
    { key: 'unplannedDowntimeHours', label: t('unplannedDowntime'), format: (v) => `${v}h / rok` },
    { key: 'failuresCaught', label: t('avgPredictionAccuracy'), format: (v) => `${v}%` },
    { key: 'avgRepairTime', label: t('avgRepairTime'), format: (v) => `${v}h` },
  ];

  const totalSavings = comparison.preventive.annualCost - comparison.prescriptive.annualCost;
  const downtimeReduction = (((comparison.preventive.unplannedDowntimeHours - comparison.prescriptive.unplannedDowntimeHours) / comparison.preventive.unplannedDowntimeHours) * 100).toFixed(0);

  return (
    <div className="space-y-6">
      {/* 3-column comparison */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {methods.map((method) => {
          const data = comparison[method.key];
          return (
            <Card key={method.key} className={`shadow-soft ${method.key === 'prescriptive' ? 'ring-2 ring-success' : ''}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-center">
                  <span className="text-lg font-bold">{method.label}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {metrics.map((metric) => (
                  <div key={metric.key} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{metric.label}</span>
                    <span className="font-semibold text-foreground">{metric.format(data[metric.key])}</span>
                  </div>
                ))}
                <div className="pt-2 border-t border-border">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Spare parts waste</span>
                    <span className="font-semibold text-foreground">{data.sparePartsWaste}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Horizontal bar chart */}
      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle>{t('annualCost')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {methods.map((method) => {
            const cost = comparison[method.key].annualCost;
            const pct = (cost / maxCost) * 100;
            return (
              <div key={method.key} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-foreground">{method.label}</span>
                  <span className="text-muted-foreground">{(cost / 1000000).toFixed(1)}M CZK</span>
                </div>
                <div className="h-6 w-full bg-muted rounded-md overflow-hidden">
                  <div
                    className="h-full rounded-md transition-all"
                    style={{ width: `${pct}%`, backgroundColor: method.color }}
                  />
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Summary */}
      <Card className="shadow-soft border-success border-2">
        <CardContent className="p-6">
          <div className="flex items-center gap-4 flex-wrap">
            <CheckCircle2 className="w-10 h-10 text-success shrink-0" />
            <div className="flex-1">
              <h3 className="text-xl font-bold text-foreground">
                {(totalSavings / 1000000).toFixed(1)}M CZK / rok
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('estimatedSavings')} (preventivni {'\u2192'} preskriptivni) &bull; -{downtimeReduction}% {t('unplannedDowntime').toLowerCase()}
              </p>
            </div>
            <ArrowRight className="w-6 h-6 text-success hidden md:block" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
